import { ComponentProps, ElementKey, ProxyProp } from '@/types/component';
import { MergeProps } from '@/types/utils';
import { merge, mergeProps } from '@/utils/merge';
import { withComponentDisplayName } from '@/utils/withComponentDisplayName';
import { ClassNameOptions, UseClassName } from 'lib/contexts/ComponentClassNameFactory';
import { withNonHTMLChildren, withSafeInnerHTML } from 'lib/utils';
import { FunctionComponent, ReactNode } from 'react';

type Styles = {
  key?: string;
  useClassName?: UseClassName;
};

export type GenericComponentProps<Element extends ElementKey> = {
  name?: string;
  styles?: Styles;
  options?: ClassNameOptions<any>;
} & ProxyProp<Element>;

type GenericFunctionComponentProps<Element extends ElementKey, Extras = never> = MergeProps<
  ComponentProps<Element>,
  Extras
> &
  React.PropsWithChildren &
  ProxyProp<Element>;

export interface GenericFunctionComponent<Element extends ElementKey> extends Pick<FunctionComponent, 'displayName'> {
  <Extras>(props: GenericFunctionComponentProps<Element, Extras>): ReactNode;
}

export type GenericComponent = <Element extends ElementKey = 'div'>(
  props: GenericComponentProps<Element>
) => GenericFunctionComponent<Element>;

export const GenericComponentFactory = (common: Required<Pick<Styles, 'useClassName'>>) => {
  return <Element extends ElementKey>({ name, ...props }: GenericComponentProps<Element>) => {
    const mergedProps = merge({ styles: { useClassName: common.useClassName } }, props);
    const Component = GenericComponent(mergedProps);

    withComponentDisplayName(Component, {
      fallbackDisplayName: name,
    });
    return Component;
  };
};

export const GenericComponent: GenericComponent = ({ as, name, styles, options }) => {
  const Component: ReturnType<GenericComponent> = ({ children, ...props }) => {
    const Tag = (props.as || as || 'div') as ElementKey;

    const className = styles?.useClassName?.(
      styles.key,
      props,
      merge(
        {
          fallbacks: {
            size: 'variant',
            colors: 'variant',
          },
        },
        options
      )
    );

    if (name) {
      Object.defineProperty(Component, 'displayName', { value: name, writable: true });
    }

    return (
      <Tag {...withSafeInnerHTML(children)} {...mergeProps({ className }, props)}>
        {withNonHTMLChildren(children)}
      </Tag>
    );
  };

  return Component;
};
